import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import convertPlainTextToHTML from '../../../../lib/convertPlainTextToHTML';

import TableDataCell from './TableDataCell';

import FieldInput from '../FieldInput';

const StyledDisplayText = styled.div`
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'white-space: nowrap;' : '')}
  ${(props) => (props.textOverflow === 'ellipsis' ? 'overflow: hidden;' : '')}
  overflow-wrap: break-word;
`;

class TableFieldNumberCell extends PureComponent {

  static propTypes = {
    id: PropTypes.string,
    disabled: PropTypes.bool,
    focused: PropTypes.bool,
    rowId: PropTypes.string,
    columnId: PropTypes.string,
    cellKey: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    width: PropTypes.number,

  }

  constructor(props) {
    super(props);
    this.state = { changes: props.value };
  }

  componentWillUnmount() {

    const { onChange, focused, value, rowId, columnId, columnIdentifier, onBlur } = this.props;
    const { changes } = this.state;

    /**
     * DO NOT REMOVE
     *
     * When a component is unmounted the blur event doesn't get fired.
     * This ensure that inputs that rely on the blur event have their
     * changes saved even when react is unmounting them.
     *
     * See: https://github.com/zionlabs/Issues/issues/1205
     *
     */
    if (onChange && focused && changes !== value && onBlur) {

      onChange(rowId, columnId, changes);

      onBlur(rowId, columnId, columnIdentifier, {});

    }

  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (prevProps.value !== value) {
      this.setState({ changes: value });
    }

  }

  render() {

    const {
      id,
      rowId,
      columnId,
      disabled,
      cellKey,
      focused,
      onFocus,
      onKeyPress,
      primaryColor,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,
      width,
      styles,
    } = this.props;

    const { changes } = this.state;

    return focused && !disabled ? (
      <TableDataCell
        key={`${cellKey}-editable`}
        data-testid={`${id}-${rowId}-${columnId}-number-cell`}
        style={this.height ? { height: `${this.height}px`, minHeight: `${this.height}px` } : undefined}
        {...{
          focused,
          width,
          padding: 0,
          borderColor,
          borderWidth,
          backgroundColor,
          primaryColor
        }}
      >
        <FieldInput
          autoFocus
          type="number"
          value={changes}
          onChange={(e) => {

            //Logic below prevents the value from being saved as a string if it is a number
            const val = e.target.value;

            let nextValue;
            if (val === '') {
              nextValue = ''; // or `null`, depending on what your app expects
            } else {
              nextValue = val.indexOf('.') === -1 ? parseInt(val, 10) : parseFloat(val, 10);
            }

            this.setState({ changes: nextValue });

          }}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onKeyDown={(e) => {
            if (onKeyPress) onKeyPress(rowId, columnId, e);
          }}
          width="100%"
          {...{
            disabled,
            focused,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderWidth,
            backgroundColor,
            padding,
            primaryColor,
            styles,
          }}
        />

      </TableDataCell>
    ) : (
      <TableDataCell
        key={`${cellKey}-preview`}
        disabled={disabled}
        data-testid={`${id}-${rowId}-${columnId}-text-cell`}
        handleCellRef={(e) => {
          /**
           * Used to make sure the rendered textarea cell
           * is the same size as the display text when
           * the text cell is focused.
           */
          if (e) {
            this.height = e.offsetHeight;
            this.width = e.offsetWidth;
          }
        }}
        onClick={() => {
          if (!disabled && onFocus) onFocus(rowId, columnId);
        }}
        {...{
          primaryColor,
          width,
          fontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          borderColor,
          borderWidth,
          backgroundColor,
          padding,
        }}
      >
        <StyledDisplayText
          {...{ textOverflow }}
          dangerouslySetInnerHTML={{ __html: this.getInnerHtml() }}
        />
      </TableDataCell>
    );

  }

  handleFocus = (e) => {

    const { onFocus, rowId, columnId, columnIdentifier, columnType, } = this.props;

    if (onFocus) {
      onFocus(rowId, columnId, columnIdentifier, columnType, { blur: () => { e.target.blur(); } });
    }

  }

  handleBlur = () => {

    const { changes } = this.state;
    const { value, onBlur, onChange, rowId, columnId, columnIdentifier } = this.props;

    if (onChange && changes !== value) onChange(rowId, columnId, changes);

    if (onBlur) onBlur(rowId, columnId, columnIdentifier, {});

  }

  getInnerHtml = () => {

    const { displayValue } = this.props;

    const parsedValue = displayValue && typeof displayValue !== 'string' ? displayValue.toString() : displayValue;

    return convertPlainTextToHTML(parsedValue);

  }

}

export default TableFieldNumberCell;
